import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Calculate as CalculateIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  Group as GroupIcon,
  RequestPage as RequestPageIcon,
  Factory as FactoryIcon,
  CreditCard as CreditCardIcon,
  Store as StoreIcon,
  MonetizationOn as MonetizationOnIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { 
  Code as CodeIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Devices as DevicesIcon,
  Storage as StorageIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
interface PropsData {
  handleClose: () => void;
}

interface ProductCategory {
  name: string;
  color: string;
  products: Product[];
}

interface Product {
  name: string;
  description: string;
  path: string;
  icon: React.ElementType;
  badge?: string;
}
interface Feature {
  title: string;
  description: string;
  icon: React.ElementType;
}
const productCategories: ProductCategory[] = [
  {
    name: 'Finance & Banking',
    color: '#2196f3',
    products: [
      {
        name: 'Gold Loan',
        description: 'Complete gold loan management system with valuation and tracking',
        path: '/gold-loan',
        icon: MonetizationOnIcon,
        badge: 'Popular',
      },
      {
        name: 'Accounting',
        description: 'Comprehensive accounting and financial management solution',
        path: '/accounting',
        icon: CalculateIcon,
      },
      {
        name: 'Finance',
        description: 'Enterprise financial planning and management system',
        path: '/finance',
        icon: AccountBalanceIcon,
      },
    ],
  },
  {
    name: 'Operations',
    color: '#4caf50',
    products: [
      {
        name: 'Lab',
        description: 'Laboratory information and test management system',
        path: '/lab',
        icon: ScienceIcon,
      },
      {
        name: 'Stockwin',
        description: 'Advanced inventory and warehouse management',
        path: '/stockwin',
        icon: InventoryIcon,
        badge: 'New',
      },
      {
        name: 'Production',
        description: 'End-to-end production and manufacturing management',
        path: '/production',
        icon: FactoryIcon,
      },
    ],
  },
  {
    name: 'Human Resources',
    color: '#9c27b0',
    products: [
      {
        name: 'HRMS',
        description: 'Complete human resource management solution',
        path: '/hrms',
        icon: GroupIcon,
        badge: 'Featured',
      },
      {
        name: 'Payroll',
        description: 'Automated salary processing and tax management',
        path: '/payroll',
        icon: RequestPageIcon,
      },
    ],
  },
  {
    name: 'Sales & Retail',
    color: '#ff9800',
    products: [
      {
        name: 'Retail',
        description: 'Point of sale and retail management system',
        path: '/retail',
        icon: StoreIcon,
      },
    ],
  },
];
const features: Feature[] = [
  {
    title: 'Innovative Technology',
    description: 'We leverage cutting-edge technology to deliver powerful, scalable solutions.',
    icon: CodeIcon,
  },
  {
    title: 'Industry Expertise',
    description: 'Deep domain knowledge across multiple industries ensures optimal solutions.',
    icon: PsychologyIcon,
  },
  {
    title: 'Customer Success',
    description: 'Were committed to your success with dedicated support and training.',
    icon: BusinessIcon,
  },
];
const AboutHuewine: React.FC<PropsData> = ({ handleClose }) => {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Hero Section */}
      <Box 
  sx={{ 
    position: 'relative',
    borderRadius: 4,
    overflow: 'hidden',
    mb: 8,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    p: { xs: 4, md: 8 },
  }}
>
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'primary.main',
      opacity: 0.8,
    }}
  />
  <Box position="relative">
    <Typography variant="h2" fontWeight="bold" gutterBottom>
      Welcome to Huewine Technologies
    </Typography>
    <Typography variant="h5" sx={{ mb: 4, maxWidth: 600 }}>
      Empowering businesses with innovative software solutions
    </Typography>
    <Box display="flex" gap={2} flexWrap="wrap">
      <Button 
        variant="contained" 
        color="secondary" 
        size="large"
        onClick={() => window.open('https://huewine.com', '_blank')}
      >
        Visit Our Website
      </Button>
      <Button 
        variant="contained" 
        color="primary" 
        size="large"
        onClick={handleClose}
        sx={{
          borderColor: 'white',
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'white',
          },
        }}
      >
        Back to Application
      </Button>
    </Box>
  </Box>
</Box>
      {/* Stats Section */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom align="center">
          Why Choose Huewine?
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <feature.icon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {feature.title}
                  </Typography>
                </Box>
                <Typography color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Products Section */}
      {productCategories.map((category) => (
        <Box key={category.name} sx={{ mb: 8 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              color: category.color,
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                display: 'block',
                width: 4,
                height: 24,
                backgroundColor: category.color,
                marginRight: 2,
                borderRadius: 1,
              }
            }}
          >
            {category.name}
          </Typography>
          <Grid container spacing={3}>
            {category.products.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.name}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${alpha(category.color, 0.2)}`,
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box 
                        sx={{ 
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(category.color, 0.1),
                          color: category.color,
                        }}
                      >
                        <product.icon sx={{ fontSize: 32 }} />
                      </Box>
                      {product.badge && (
                        <Chip 
                          label={product.badge} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(category.color, 0.1),
                            color: category.color,
                            fontWeight: 'bold',
                          }} 
                        />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {product.description}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => window.open(product.path, '_blank')}
                      sx={{ 
                        color: category.color,
                        borderColor: category.color,
                        '&:hover': {
                          borderColor: category.color,
                          bgcolor: alpha(category.color, 0.1),
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Contact Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          borderRadius: 2,
          background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%)',
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Ready to Transform Your Business?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Get in touch with us to learn how our solutions can help your business grow.
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Phone:</strong> +91 4651-292792, +91-9789441792
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> support@huewine.com
              </Typography>
              <Typography variant="body2">
                <strong>Location:</strong> Kanavoor, Karungal, Kanniyakumari, Tamil Nadu 629178
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5} container justifyContent="flex-end">
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={() => window.open('https://huewine.com/contact.html', '_blank')}
            >
              Contact Us
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box mt={4} textAlign="center">
        <Typography variant="body2" color="text.disabled">
          © {new Date().getFullYear()} Huewine Technologies Pvt. Ltd. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default AboutHuewine;